import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DateField } from '../dropdown';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type {
  DistiListingAuth,
  DistiListingColumnMap,
  DistiListingConfigUpdate,
  DistiListingDeleteResponse,
  DistiListingFormat,
  DistiListingPreviewResponse,
  DistiListingPullResponse,
  ListingExclusionRule,
  ListingRule,
  Vendor,
} from '@techgitter/contracts';
import {
  AllFilteredError,
  deleteDistiListings,
  previewDistiListingPull,
  previewDistiListingUpload,
  runDistiListingPull,
  uploadDistiListingFile,
} from '../../services/distiListingsApi';
import { RuleListEditor } from './RuleListEditor';

interface DistiListingConfigPanelProps {
  vendor: Vendor;
  /**
   * Fired on every config edit. Carries the current draft (or `null` when
   * the JSON editors fail to parse) and a `dirty` flag so the parent dialog
   * can drive its single Save button. The parent owns the persistence
   * call — this panel never saves directly.
   */
  onChange: (
    draft: DistiListingConfigUpdate | null,
    state: { dirty: boolean; valid: boolean },
  ) => void;
}

const DEFAULT_COLUMN_MAP: DistiListingColumnMap = {
  vendorSku: 'sku',
  description: 'name',
  category: 'category',
  subcategory: 'subcategory',
  spec: 'description',
  cost: 'cost',
  shipping: 'shipping',
  stock: 'stock',
  url: 'url',
  imageUrl: 'image',
};

const DEFAULT_AUTH: DistiListingAuth = { type: 'none' };

const formatTime = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU');
  } catch {
    return iso;
  }
};

const formatCount = (n: number): string => n.toLocaleString('en-AU');

export const DistiListingConfigPanel: React.FC<
  DistiListingConfigPanelProps
> = ({ vendor, onChange }) => {
  const initial = vendor.listing;
  const [enabled, setEnabled] = useState(initial?.enabled ?? false);
  const [url, setUrl] = useState(initial?.url ?? '');
  const [format, setFormat] = useState<DistiListingFormat>(
    initial?.format ?? 'csv',
  );
  const [auth, setAuth] = useState<DistiListingAuth>(initial?.auth ?? DEFAULT_AUTH);
  const [columnMap, setColumnMap] = useState<DistiListingColumnMap>(
    initial?.columnMap ?? DEFAULT_COLUMN_MAP,
  );
  const [productUrlTemplate, setProductUrlTemplate] = useState<string>(
    initial?.productUrlTemplate ?? '',
  );
  const [exclusionRules, setExclusionRules] = useState<ListingExclusionRule[]>(
    initial?.exclusionRules ?? [],
  );
  const [rules, setRules] = useState<ListingRule[]>(initial?.rules ?? []);
  // Validity bits track JSON-mode parse errors inside each editor. Form
  // mode is always valid; JSON mode flips this off until the textarea
  // parses cleanly again.
  const [exclusionValid, setExclusionValid] = useState(true);
  const [rulesValid, setRulesValid] = useState(true);

  // Baseline is the vendor row at mount/refresh — used to compute dirty
  // by comparing current state against the originally-loaded config. Rule
  // arrays are serialised via JSON for cheap structural comparison.
  const baselineRef = React.useRef({
    enabled: vendor.listing?.enabled ?? false,
    url: vendor.listing?.url ?? '',
    format: (vendor.listing?.format ?? 'csv') as DistiListingFormat,
    auth: vendor.listing?.auth ?? DEFAULT_AUTH,
    columnMap: vendor.listing?.columnMap ?? DEFAULT_COLUMN_MAP,
    productUrlTemplate: vendor.listing?.productUrlTemplate ?? '',
    exclusionRulesJson: JSON.stringify(vendor.listing?.exclusionRules ?? []),
    rulesJson: JSON.stringify(vendor.listing?.rules ?? []),
  });

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<DistiListingPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [pulling, setPulling] = useState(false);
  const [pullStatus, setPullStatus] = useState<DistiListingPullResponse | null>(
    null,
  );
  const [pullError, setPullError] = useState<string | null>(null);
  // When checked, the next Run-now bypasses the hash-equality short-circuit
  // so every classified row's derived fields (specs + calculated description)
  // get re-extracted. Used after extractor/generator upgrades.
  const [forceRewrite, setForceRewrite] = useState(false);
  const [forceConfirm, setForceConfirm] = useState<{
    counts: Record<string, number>;
    message: string;
    // The retry path is captured at trigger time so "Run anyway" knows
    // whether to re-run as a URL pull or as a re-upload of the same file.
    retry: () => Promise<void>;
  } | null>(null);

  // Delete-listings state
  type DeleteMode = 'sku' | 'range';
  const [deleteMode, setDeleteMode] = useState<DeleteMode>('sku');
  const [deleteSku, setDeleteSku] = useState('');
  const [deleteSeenBefore, setDeleteSeenBefore] = useState('');
  const [deleteSeenAfter, setDeleteSeenAfter] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteResult, setDeleteResult] = useState<DistiListingDeleteResponse | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    matched: number;
    body: {
      distiSku?: string | null;
      seenBefore?: string;
      seenAfter?: string;
    };
  } | null>(null);

  // Reset local state when the parent vendor row changes (e.g. after save).
  // Reseating the baselineRef here also resets the dirty flag — the latest
  // saved vendor row becomes "the baseline" for future edits.
  useEffect(() => {
    const nextEnabled = vendor.listing?.enabled ?? false;
    const nextUrl = vendor.listing?.url ?? '';
    const nextFormat = (vendor.listing?.format ?? 'csv') as DistiListingFormat;
    const nextAuth = vendor.listing?.auth ?? DEFAULT_AUTH;
    const nextColumnMap = vendor.listing?.columnMap ?? DEFAULT_COLUMN_MAP;
    const nextProductUrlTemplate = vendor.listing?.productUrlTemplate ?? '';
    const nextExclusionRules = vendor.listing?.exclusionRules ?? [];
    const nextRules = vendor.listing?.rules ?? [];

    setEnabled(nextEnabled);
    setUrl(nextUrl);
    setFormat(nextFormat);
    setAuth(nextAuth);
    setColumnMap(nextColumnMap);
    setProductUrlTemplate(nextProductUrlTemplate);
    setExclusionRules(nextExclusionRules);
    setRules(nextRules);
    setExclusionValid(true);
    setRulesValid(true);
    setPreview(null);
    setPullStatus(null);

    baselineRef.current = {
      enabled: nextEnabled,
      url: nextUrl,
      format: nextFormat,
      auth: nextAuth,
      columnMap: nextColumnMap,
      productUrlTemplate: nextProductUrlTemplate,
      exclusionRulesJson: JSON.stringify(nextExclusionRules),
      rulesJson: JSON.stringify(nextRules),
    };
  }, [vendor]);

  // The current draft, or null when one of the rule editors is in JSON
  // mode with an unparseable textarea. Memoised so child effects/buttons
  // can read it without re-derivation.
  const draft = useMemo<DistiListingConfigUpdate | null>(() => {
    if (!exclusionValid || !rulesValid) return null;
    return {
      enabled,
      url,
      format,
      auth,
      columnMap,
      productUrlTemplate,
      exclusionRules,
      rules,
    };
  }, [
    enabled,
    url,
    format,
    auth,
    columnMap,
    productUrlTemplate,
    exclusionRules,
    rules,
    exclusionValid,
    rulesValid,
  ]);

  const isDirty = useMemo(() => {
    const b = baselineRef.current;
    return (
      enabled !== b.enabled ||
      url !== b.url ||
      format !== b.format ||
      JSON.stringify(auth) !== JSON.stringify(b.auth) ||
      JSON.stringify(columnMap) !== JSON.stringify(b.columnMap) ||
      productUrlTemplate !== b.productUrlTemplate ||
      JSON.stringify(exclusionRules) !== b.exclusionRulesJson ||
      JSON.stringify(rules) !== b.rulesJson
    );
  }, [
    enabled,
    url,
    format,
    auth,
    columnMap,
    productUrlTemplate,
    exclusionRules,
    rules,
  ]);

  // Emit on every change so the parent dialog can drive its single Save.
  useEffect(() => {
    onChange(draft, { dirty: isDirty, valid: draft !== null });
  }, [draft, isDirty, onChange]);

  const handlePreview = async () => {
    if (!draft) return;
    setPreviewError(null);
    setPreview(null);
    setPreviewing(true);
    try {
      const result = await previewDistiListingPull(vendor.vendorId, draft);
      setPreview(result);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setPreviewing(false);
    }
  };

  // Preview against an uploaded file. The draft (unsaved) config rides
  // along in a header so techs can iterate on rules — edit, re-pick
  // the same file, see updated counts — without saving each time.
  const previewUploadInputRef = React.useRef<HTMLInputElement | null>(null);

  const handlePreviewUploadClick = () => {
    previewUploadInputRef.current?.click();
  };

  const handlePreviewUploadChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPreviewError(null);
    setPreview(null);
    setPreviewing(true);
    try {
      const result = await previewDistiListingUpload(
        vendor.vendorId,
        file,
        draft,
      );
      setPreview(result);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setPreviewing(false);
    }
  };

  // ----- Delete listings -----
  const buildDeleteBody = (): {
    distiSku?: string | null;
    seenBefore?: string;
    seenAfter?: string;
  } | null => {
    if (deleteMode === 'sku') {
      // distiSku = '' is the intentional ghost-row case — pass through.
      return { distiSku: deleteSku };
    }
    const body: { seenBefore?: string; seenAfter?: string } = {};
    if (deleteSeenBefore) body.seenBefore = `${deleteSeenBefore}T00:00:00Z`;
    if (deleteSeenAfter) body.seenAfter = `${deleteSeenAfter}T00:00:00Z`;
    if (!body.seenBefore && !body.seenAfter) {
      setDeleteError('Pick at least one date.');
      return null;
    }
    return body;
  };

  const previewDelete = async () => {
    setDeleteError(null);
    setDeleteResult(null);
    const body = buildDeleteBody();
    if (!body) return;
    setDeleteBusy(true);
    try {
      const result = await deleteDistiListings(vendor.vendorId, {
        ...body,
        dryRun: true,
      });
      setDeleteConfirm({ matched: result.matchedCount, body });
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const result = await deleteDistiListings(
        vendor.vendorId,
        deleteConfirm.body,
      );
      setDeleteResult(result);
      setDeleteConfirm(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleteBusy(false);
    }
  };

  const runPull = async (force: boolean) => {
    setPullError(null);
    setPullStatus(null);
    setPulling(true);
    try {
      const result = await runDistiListingPull(vendor.vendorId, {
        force,
        forceRewrite,
      });
      setPullStatus(result);
      setForceConfirm(null);
    } catch (err) {
      if (err instanceof AllFilteredError) {
        setForceConfirm({
          counts: err.counts,
          message: err.message,
          retry: () => runPull(true),
        });
      } else {
        setPullError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setPulling(false);
    }
  };

  // File-upload path — for vendors whose feed sits behind a login
  // the cron can't reach. Mirrors `runPull` but POSTs file bytes
  // instead of letting the server fetch the URL.
  const uploadInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

  const runUpload = async (file: File, force: boolean) => {
    setPullError(null);
    setPullStatus(null);
    setPulling(true);
    setUploadedFileName(file.name);
    try {
      const result = await uploadDistiListingFile(vendor.vendorId, file, {
        force,
        forceRewrite,
      });
      setPullStatus(result);
      setForceConfirm(null);
    } catch (err) {
      if (err instanceof AllFilteredError) {
        setForceConfirm({
          counts: err.counts,
          message: err.message,
          retry: () => runUpload(file, true),
        });
      } else {
        setPullError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setPulling(false);
    }
  };

  const handleUploadButtonClick = () => {
    uploadInputRef.current?.click();
  };

  const handleUploadFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      void runUpload(file, false);
    }
    // Reset so re-selecting the same filename retriggers onChange.
    e.target.value = '';
  };

  return (
    <Box>
      <Accordion
        defaultExpanded={
          Boolean(vendor.listing?.enabled) ||
          Boolean(vendor.listing?.url)
        }
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">Disti listings feed</Typography>
            <Chip
              size="small"
              label={enabled ? 'Enabled' : 'Disabled'}
              color={enabled ? 'success' : 'default'}
            />
            {vendor.listingRunStatus?.lastRunAt && (
              <Typography variant="caption" color="text.secondary">
                Last run {formatTime(vendor.listingRunStatus.lastRunAt)} (
                {vendor.listingRunStatus.lastRunStatus})
              </Typography>
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
              }
              label="Pull this disti's listings daily"
            />

            <TextField
              label="Feed URL"
              size="small"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://disti.example/pricing.csv"
              fullWidth
            />

            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel id="feed-format-label">Format</InputLabel>
              <Select
                labelId="feed-format-label"
                label="Format"
                value={format}
                onChange={(e) => setFormat(e.target.value as DistiListingFormat)}
              >
                <MenuItem value="csv">CSV</MenuItem>
                <MenuItem value="tsv">TSV (tab-delimited)</MenuItem>
                <MenuItem value="json">JSON</MenuItem>
              </Select>
            </FormControl>

            <Divider />
            <Typography variant="overline">Authentication</Typography>
            <FormControl size="small" sx={{ width: 220 }}>
              <InputLabel id="auth-type-label">Auth type</InputLabel>
              <Select
                labelId="auth-type-label"
                label="Auth type"
                value={auth.type}
                onChange={(e) =>
                  setAuth({ type: e.target.value as DistiListingAuth['type'] })
                }
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="header">Custom header</MenuItem>
                <MenuItem value="bearer">Bearer token</MenuItem>
                <MenuItem value="basic">Basic auth</MenuItem>
              </Select>
            </FormControl>

            {auth.type === 'header' && (
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="Header name"
                  value={auth.headerName ?? ''}
                  onChange={(e) =>
                    setAuth({ ...auth, headerName: e.target.value })
                  }
                  sx={{ width: 240 }}
                />
                <TextField
                  size="small"
                  label="Header value"
                  type="password"
                  placeholder="(stored encrypted)"
                  value={auth.headerValue ?? ''}
                  onChange={(e) =>
                    setAuth({ ...auth, headerValue: e.target.value })
                  }
                  fullWidth
                />
              </Stack>
            )}
            {auth.type === 'bearer' && (
              <TextField
                size="small"
                label="Bearer token"
                type="password"
                placeholder="(stored encrypted)"
                value={auth.token ?? ''}
                onChange={(e) => setAuth({ ...auth, token: e.target.value })}
                fullWidth
              />
            )}
            {auth.type === 'basic' && (
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="Username"
                  value={auth.username ?? ''}
                  onChange={(e) =>
                    setAuth({ ...auth, username: e.target.value })
                  }
                  sx={{ width: 240 }}
                />
                <TextField
                  size="small"
                  label="Password"
                  type="password"
                  value={auth.password ?? ''}
                  onChange={(e) =>
                    setAuth({ ...auth, password: e.target.value })
                  }
                  fullWidth
                />
              </Stack>
            )}

            <Divider />
            <Typography variant="overline">Column / path map</Typography>
            <Typography variant="caption" color="text.secondary">
              For CSV: supplier column header. For JSON: dotted path.
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {(
                [
                  'vendorSku',
                  'manufacturer',
                  'manufacturerCode',
                  'warranty',
                  'description',
                  'category',
                  'subcategory',
                  'subcategory1',
                  'subcategory2',
                  'subcategory3',
                  'spec',
                  'cost',
                  'rrpEx',
                  'rrpInc',
                  'shipping',
                  'stock',
                  'stockQld',
                  'stockNsw',
                  'stockSa',
                  'url',
                  'imageUrl',
                ] as const
              ).map((field) => (
                <TextField
                  key={field}
                  size="small"
                  label={field}
                  value={(columnMap as Record<string, string | undefined>)[field] ?? ''}
                  onChange={(e) =>
                    setColumnMap({
                      ...columnMap,
                      [field]: e.target.value,
                    })
                  }
                  sx={{ width: 200 }}
                />
              ))}
            </Stack>

            <Divider />
            <Typography variant="overline">Product URL template</Typography>
            <Typography variant="caption" color="text.secondary">
              Optional — when set, always overrides the per-row <code>url</code>{' '}
              from the feed. Tokens: <code>{'{vendorSku}'}</code>{' '}
              and <code>{'{manufacturerCode}'}</code>.
            </Typography>
            <TextField
              size="small"
              placeholder="https://shop.example.com/product/{vendorSku}"
              value={productUrlTemplate}
              onChange={(e) => setProductUrlTemplate(e.target.value)}
              fullWidth
            />

            <Divider />
            <Typography variant="overline">Exclusion rules</Typography>
            <Typography variant="caption" color="text.secondary">
              Walked first — matching rows are dropped before classification.
              Same condition shape as classification rules but no <code>code</code>.
            </Typography>
            <RuleListEditor
              kind="exclusion"
              value={exclusionRules}
              onChange={setExclusionRules}
              onValidityChange={setExclusionValid}
            />

            <Divider />
            <Typography variant="overline">Classification rules</Typography>
            <Typography variant="caption" color="text.secondary">
              Walked top-down on survivors. First match wins. Rows that match
              no rule are dropped (strict-filter ingest). Click <strong>Preview</strong> to
              inspect before running.
            </Typography>
            <RuleListEditor
              kind="classification"
              value={rules}
              onChange={setRules}
              onValidityChange={setRulesValid}
            />

            <Divider />
            <Typography variant="caption" color="text.secondary">
              Preview runs the pipeline against your unsaved changes — no
              persistence, no run-status update. Run now uses the last
              <strong> saved</strong> config, so save first if you've edited.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handlePreview}
                disabled={previewing || !url || draft === null}
              >
                {previewing ? <CircularProgress size={16} /> : 'Preview'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handlePreviewUploadClick}
                disabled={previewing || draft === null}
                title={
                  draft === null
                    ? 'Fix the rule errors before previewing'
                    : 'Dry-run the pipeline against an uploaded file plus your CURRENT (unsaved) draft rules. No persistence — counts + samples only.'
                }
              >
                {previewing ? <CircularProgress size={16} /> : 'Preview file'}
              </Button>
              <input
                ref={previewUploadInputRef}
                type="file"
                onChange={handlePreviewUploadChange}
                style={{ display: 'none' }}
              />
              <Button
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={() => runPull(false)}
                disabled={pulling || !url || isDirty}
                title={
                  isDirty
                    ? 'Save your changes before running the pull'
                    : !url
                    ? 'Set the feed URL first'
                    : undefined
                }
              >
                {pulling ? <CircularProgress size={16} /> : 'Run now'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleUploadButtonClick}
                disabled={pulling}
                title={
                  isDirty
                    ? 'Upload uses the last SAVED config (column map, format, rules). Save your edits first if you want them applied to this upload.'
                    : 'Upload a feed file (CSV / TSV / TXT / ZIP) — for vendors whose feed sits behind a login'
                }
              >
                {pulling && uploadedFileName
                  ? <CircularProgress size={16} />
                  : 'Upload file'}
              </Button>
              <input
                ref={uploadInputRef}
                type="file"
                onChange={handleUploadFileChange}
                style={{ display: 'none' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={forceRewrite}
                    onChange={(e) => setForceRewrite(e.target.checked)}
                    disabled={pulling}
                  />
                }
                label="Refresh all derived fields"
                title="Bypass the hash short-circuit so every classified row re-extracts its specs and regenerates the calculated description. Use this after extractor or description-generator upgrades."
                sx={{ ml: 1 }}
              />
            </Stack>

            {previewError && <Alert severity="error">{previewError}</Alert>}
            {pullError && <Alert severity="error">{pullError}</Alert>}

            {preview && (
              <PreviewResultPanel preview={preview} />
            )}

            {pullStatus && (
              <Alert
                severity={pullStatus.status === 'error' ? 'error' : 'success'}
              >
                <strong>Run complete</strong> — {pullStatus.message}
              </Alert>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* ============================================================
          Delete listings
          ============================================================ */}
      <Accordion sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <DeleteSweepIcon fontSize="small" color="action" />
            <Typography variant="subtitle2">Delete listings</Typography>
            <Typography variant="caption" color="text.secondary">
              Bulk-remove rows by SKU or seen-at range
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="caption" color="text.secondary">
              Hard-deletes rows from <code>cr001_disti_listings</code> in
              batched <code>$batch</code> envelopes. The next pull will
              re-create any rows still in the feed — useful for ghost-row
              cleanup (empty SKU) or stale-row purge.
            </Typography>

            <FormControl size="small" sx={{ width: 240 }}>
              <InputLabel id="delete-mode-label">Delete by</InputLabel>
              <Select
                labelId="delete-mode-label"
                label="Delete by"
                value={deleteMode}
                onChange={(e) => {
                  setDeleteMode(e.target.value as DeleteMode);
                  setDeleteError(null);
                  setDeleteResult(null);
                }}
              >
                <MenuItem value="sku">Single SKU</MenuItem>
                <MenuItem value="range">Seen-at date range</MenuItem>
              </Select>
            </FormControl>

            {deleteMode === 'sku' && (
              <TextField
                size="small"
                label="Disti SKU"
                placeholder="(empty = clean up ghost rows)"
                value={deleteSku}
                onChange={(e) => setDeleteSku(e.target.value)}
                helperText="Leave blank to target rows with empty SKU."
              />
            )}

            {deleteMode === 'range' && (
              <Stack direction="row" spacing={1}>
                <DateField
                  label="Seen before"
                  value={deleteSeenBefore || null}
                  onChange={(iso) => setDeleteSeenBefore(iso ?? '')}
                  helperText="Delete rows last seen before this date"
                />
                <DateField
                  label="Seen after"
                  value={deleteSeenAfter || null}
                  onChange={(iso) => setDeleteSeenAfter(iso ?? '')}
                  helperText="Delete rows last seen on/after this date"
                />
              </Stack>
            )}

            <Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteSweepIcon />}
                onClick={previewDelete}
                disabled={deleteBusy}
              >
                {deleteBusy ? <CircularProgress size={16} /> : 'Find matches…'}
              </Button>
            </Box>

            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            {deleteResult && (
              <Alert severity={deleteResult.failedCount > 0 ? 'warning' : 'success'}>
                <strong>Deleted {deleteResult.deletedCount}</strong>
                {' '}of {deleteResult.matchedCount} matched
                {deleteResult.failedCount > 0
                  ? ` — ${deleteResult.failedCount} failed (${deleteResult.firstFailure ?? 'unknown'})`
                  : '.'}
              </Alert>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Confirm-delete dialog (two-step: preview → confirm) */}
      <Dialog
        open={Boolean(deleteConfirm)}
        onClose={() => !deleteBusy && setDeleteConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete {deleteConfirm?.matched ?? 0} listing(s)?</DialogTitle>
        <DialogContent>
          {deleteConfirm?.matched === 0 ? (
            <DialogContentText>
              No listings matched the criteria. Nothing to delete.
            </DialogContentText>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                You're about to <strong>permanently delete{' '}
                {deleteConfirm?.matched.toLocaleString('en-AU')}</strong>{' '}
                listing row(s) for {vendor.name}. This cannot be undone.
              </DialogContentText>
              <Alert severity="info">
                The next feed pull will re-create any rows that are still
                present in the supplier's feed.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleteBusy}>
            Cancel
          </Button>
          {deleteConfirm && deleteConfirm.matched > 0 && (
            <Button
              variant="contained"
              color="error"
              onClick={confirmDelete}
              disabled={deleteBusy}
            >
              {deleteBusy ? <CircularProgress size={16} /> : 'Delete'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(forceConfirm)}
        onClose={() => setForceConfirm(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>All rows would be filtered out</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {forceConfirm?.message}
          </DialogContentText>
          {forceConfirm && (
            <CountsRow counts={forceConfirm.counts} />
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            Forcing the run will leave existing listings stale (their{' '}
            <code>seen_at</code> won't advance).
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => forceConfirm?.retry()}
            disabled={pulling}
          >
            Run anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Result panels
// ---------------------------------------------------------------------------

const CountsRow: React.FC<{ counts: Record<string, number> }> = ({ counts }) => (
  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
    <Chip label={`Seen: ${formatCount(counts.rowsSeen ?? 0)}`} />
    {(counts.rowsSkippedNoSku ?? 0) > 0 && (
      <Chip
        label={`No SKU: ${formatCount(counts.rowsSkippedNoSku)}`}
        color="error"
        variant="outlined"
      />
    )}
    <Chip label={`Excluded: ${formatCount(counts.rowsExcluded ?? 0)}`} color="default" />
    <Chip
      label={`Classified: ${formatCount(counts.rowsClassified ?? 0)}`}
      color="success"
      variant="outlined"
    />
    <Chip
      label={`Unclassified: ${formatCount(counts.rowsUnclassifiedDropped ?? 0)}`}
      color="warning"
      variant="outlined"
    />
    {(counts.rowsFailed ?? 0) > 0 && (
      <Chip
        label={`Failed: ${formatCount(counts.rowsFailed)}`}
        color="error"
      />
    )}
  </Stack>
);

const PreviewResultPanel: React.FC<{ preview: DistiListingPreviewResponse }> = ({
  preview,
}) => {
  const totalClassified = Object.entries(preview.samples.classifiedByCode);

  return (
    <Stack spacing={1}>
      <CountsRow counts={preview as unknown as Record<string, number>} />

      {/* Unclassified surfaces first — most useful for refining rules. */}
      <Accordion defaultExpanded={preview.rowsUnclassifiedDropped > 0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            Unclassified dropped ({formatCount(preview.rowsUnclassifiedDropped)})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary">
            These rows survived exclusion but matched no classification rule —
            they would be dropped on Run-now.
          </Typography>
          <SampleRows rows={preview.samples.unclassifiedDropped} />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            Excluded ({formatCount(preview.rowsExcluded)})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <SampleRows rows={preview.samples.excluded} />
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            Classified ({formatCount(preview.rowsClassified)})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            {totalClassified.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No classified rows yet. Add a classification rule and Preview again.
              </Typography>
            )}
            {totalClassified.map(([code, rows]) => (
              <Box key={code}>
                <Chip label={code} size="small" sx={{ mb: 0.5 }} />
                <SampleRows rows={rows} />
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

const SampleRows: React.FC<{ rows: { row: Record<string, unknown>; rule?: unknown }[] }> = ({
  rows,
}) => (
  <Stack spacing={0.5}>
    {rows.length === 0 && (
      <Typography variant="caption" color="text.secondary">
        (no samples)
      </Typography>
    )}
    {rows.map((r, idx) => (
      <Box
        key={idx}
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          bgcolor: 'action.hover',
          p: 0.5,
          borderRadius: 1,
          overflow: 'auto',
        }}
      >
        {JSON.stringify(r.row)}
      </Box>
    ))}
  </Stack>
);
